/**
 * AdminBillingPage.js — /admin/billing
 *
 * P1.8 — Billing / Pricing Governance launch-watch dashboard.
 *
 * Read-only operator surface that aggregates everything the team
 * needs visible on day-1 of going live with real money:
 *
 *   - Revenue (24h / 7d / 30d) split top-up vs subscription
 *   - MRR + active-subscription mix
 *   - Pricing source of truth (plans, top-ups, action credit costs)
 *   - Stripe price-map status (boot-validator drift)
 *   - Failed payments + Stripe-mismatch anomalies
 *   - Marketing pricing drift alert (compares /app/marketing
 *     pricing.astro to the canonical backend catalogue)
 *   - Refund / dispute placeholder section (P1.9 follow-up)
 *
 * Hard rails (locked by spec):
 *   - No buttons that mutate Stripe / billing / DNS
 *   - No live Stripe pricing edits
 *   - All data comes from `GET /api/admin/billing/*`
 *   - `WorkspaceGuard workspace="admin"` at the route level +
 *     `require_admin` server-side
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ShieldAlert, Wallet, TrendingUp, Users,
  CreditCard, AlertTriangle, CheckCircle2, FileWarning, Sparkles,
  ArrowUpRight, Receipt, Layers, Globe, Lock, Pencil, Check, X as XIcon,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function fmtGbp(pence) {
  if (pence === null || pence === undefined || Number.isNaN(Number(pence))) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  }).format(Number(pence) / 100);
}

function fmtNum(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-GB').format(Number(n));
}

function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  if (s === 'paid' || s === 'finalised' || s === 'processed' || s === 'active') {
    return <Badge className="bg-emerald-500/10 text-emerald-200 border-emerald-500/40" variant="outline">{status}</Badge>;
  }
  if (s === 'failed' || s === 'tampered' || s === 'rejected' || s === 'expired') {
    return <Badge className="bg-rose-500/10 text-rose-200 border-rose-500/40" variant="outline">{status}</Badge>;
  }
  if (s === 'pending' || s === 'received' || s === 'trialing') {
    return <Badge className="bg-amber-500/10 text-amber-200 border-amber-500/40" variant="outline">{status}</Badge>;
  }
  return <Badge variant="outline" className="border-ndor-edge text-ndor-ink-secondary">{status || '—'}</Badge>;
}

// ─────────────────────────────────────────────────────────────
// KPI Card
// ─────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sublabel, hint, testid, tone = 'neutral' }) {
  const toneClass =
    tone === 'gold' ? 'text-ndor-gold' :
    tone === 'good' ? 'text-emerald-600' :
    tone === 'warn' ? 'text-amber-600' :
    'text-ndor-ink';
  return (
    <Card className="border-ndor-edge bg-ndor-surface" data-testid={testid}>
      <CardContent className="p-5 space-y-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">
          <Icon className="h-3.5 w-3.5" />
          {label}
        </div>
        <p className={`text-3xl sm:text-4xl font-serif-display font-semibold tabular-nums leading-none ${toneClass}`}>
          {value}
        </p>
        {sublabel ? (
          <p className="text-xs text-ndor-ink-secondary">{sublabel}</p>
        ) : null}
        {hint ? (
          <p className="text-[11px] text-ndor-ink-muted font-mono-ui">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
export default function AdminBillingPage() {
  const [overview, setOverview] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [anomalies, setAnomalies] = useState(null);
  const [drift, setDrift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const [ov, pr, tx, ev, an, dr] = await Promise.all([
        apiFetch('/api/admin/billing/overview'),
        apiFetch('/api/admin/billing/pricing-snapshot'),
        apiFetch('/api/admin/billing/transactions?limit=50'),
        apiFetch('/api/admin/billing/webhook-events?limit=50'),
        apiFetch('/api/admin/billing/anomalies'),
        apiFetch('/api/admin/billing/marketing-drift'),
      ]);
      setOverview(ov);
      setPricing(pr);
      setTransactions(tx?.transactions || []);
      setWebhookEvents(ev?.events || []);
      setAnomalies(an);
      setDrift(dr);
      setFetchedAt(new Date());
    } catch (e) {
      if (e?.status === 403) {
        setForbidden(true);
      } else {
        const msg = e?.message || 'Failed to load billing data.';
        setError(msg);
        if (silent) toast.error('Refresh failed', { description: msg });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  // Anomaly count for the warning header
  const anomalyTotal = useMemo(() => {
    const s = anomalies?.summary || {};
    return (s.stale_pending_count || 0)
      + (s.paid_no_finalise_count || 0)
      + (s.paid_no_event_count || 0)
      + (s.rejected_webhook_count || 0)
      + (s.missing_stripe_map_count || 0);
  }, [anomalies]);

  if (forbidden) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 px-4 text-center space-y-3"
        data-testid="admin-billing-forbidden"
      >
        <ShieldAlert className="h-10 w-10 text-ndor-warning" />
        <h2 className="text-xl font-serif-display font-semibold text-ndor-ink">Admin only</h2>
        <p className="text-sm text-ndor-ink-secondary max-w-md">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="admin-billing-loading">
        <Loader2 className="h-6 w-6 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  return (
    <div data-testid="admin-billing-page" className="space-y-6 sm:space-y-8">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">
            Admin · Operations
          </p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-serif-display font-semibold tracking-tight text-ndor-ink">
            Billing & Pricing Governance
          </h1>
          <p className="mt-1.5 text-sm text-ndor-ink-secondary max-w-2xl">
            Read-only launch-watch view. Source of truth:{' '}
            <code className="px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">core/pricing.py</code>{' '}
            and Stripe Live. No mutations from this surface.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {fetchedAt ? (
            <span className="text-[11px] text-ndor-ink-muted font-mono-ui hidden sm:inline">
              Fetched: {fetchedAt.toLocaleTimeString()}
            </span>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(true)}
            disabled={refreshing}
            className="border-ndor-edge"
            data-testid="admin-billing-refresh"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </header>

      {error ? (
        <div className="rounded-lg border border-ndor-error-border bg-ndor-error-light text-ndor-error p-4 text-sm" data-testid="admin-billing-error">
          {error}
        </div>
      ) : null}

      {/* Mode + map status banner */}
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono-ui" data-testid="admin-billing-mode-banner">
        <Badge
          variant="outline"
          className={overview?.stripe_test_mode
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
            : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'}
          data-testid="admin-billing-stripe-mode"
        >
          <Lock className="h-3 w-3 mr-1.5" />
          Stripe: {overview?.stripe_test_mode ? 'TEST mode' : 'LIVE mode'}
        </Badge>
        <Badge
          variant="outline"
          className={overview?.stripe_price_map_size > 0
            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
            : 'border-rose-500/40 bg-rose-500/10 text-rose-200'}
          data-testid="admin-billing-pricemap-status"
        >
          <Layers className="h-3 w-3 mr-1.5" />
          Stripe price map: {overview?.stripe_price_map_size ?? 0} entries
        </Badge>
        {anomalyTotal > 0 ? (
          <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-200" data-testid="admin-billing-anomaly-banner">
            <AlertTriangle className="h-3 w-3 mr-1.5" />
            {anomalyTotal} anomal{anomalyTotal === 1 ? 'y' : 'ies'} surfaced
          </Badge>
        ) : (
          <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200" data-testid="admin-billing-anomaly-banner">
            <CheckCircle2 className="h-3 w-3 mr-1.5" />
            No anomalies
          </Badge>
        )}
        {drift?.available && drift?.drift_count > 0 ? (
          <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-rose-200" data-testid="admin-billing-drift-banner">
            <FileWarning className="h-3 w-3 mr-1.5" />
            Marketing drift: {drift.drift_count}
          </Badge>
        ) : null}
        {drift?.available && drift?.drift_count === 0 ? (
          <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200" data-testid="admin-billing-drift-banner">
            <CheckCircle2 className="h-3 w-3 mr-1.5" />
            Marketing pricing in sync
          </Badge>
        ) : null}
      </div>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="admin-billing-kpis">
        <KpiCard
          icon={TrendingUp}
          label="Revenue · 24h"
          value={fmtGbp(overview?.revenue?.last_24h?.pence)}
          sublabel={`${fmtNum(overview?.revenue?.last_24h?.count)} paid txns`}
          tone="gold"
          testid="admin-billing-kpi-rev24h"
        />
        <KpiCard
          icon={TrendingUp}
          label="Revenue · 7d"
          value={fmtGbp(overview?.revenue?.last_7d?.pence)}
          sublabel={`${fmtNum(overview?.revenue?.last_7d?.count)} paid txns`}
          testid="admin-billing-kpi-rev7d"
        />
        <KpiCard
          icon={TrendingUp}
          label="Revenue · 30d"
          value={fmtGbp(overview?.revenue?.last_30d?.pence)}
          sublabel={`${fmtNum(overview?.revenue?.last_30d?.count)} paid txns`}
          testid="admin-billing-kpi-rev30d"
        />
        <KpiCard
          icon={Wallet}
          label="MRR (estimate)"
          value={fmtGbp(overview?.subscriptions?.mrr_pence)}
          sublabel={`${fmtNum(overview?.subscriptions?.active_total)} active subs`}
          hint={`${fmtNum(overview?.subscriptions?.cancel_at_period_end)} pending cancel`}
          tone="good"
          testid="admin-billing-kpi-mrr"
        />
        <KpiCard
          icon={CreditCard}
          label="Top-ups · 30d"
          value={fmtGbp(overview?.revenue?.topups_30d?.pence)}
          sublabel={`${fmtNum(overview?.revenue?.topups_30d?.count)} packs sold`}
          testid="admin-billing-kpi-topups"
        />
        <KpiCard
          icon={ArrowUpRight}
          label="Subscriptions · 30d"
          value={fmtGbp(overview?.revenue?.plans_30d?.pence)}
          sublabel={`${fmtNum(overview?.revenue?.plans_30d?.count)} sessions`}
          hint="Subscription revenue is also recognised on invoice events; this is checkout-recorded only."
          testid="admin-billing-kpi-subs"
        />
        <KpiCard
          icon={Users}
          label="Paying customers · 30d"
          value={fmtNum(overview?.customers?.paying_last_30d)}
          sublabel="distinct user_ids"
          testid="admin-billing-kpi-paying"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Failed payments · 30d"
          value={fmtNum(overview?.failed_payments?.last_30d_count)}
          sublabel={(overview?.failed_payments?.last_30d_count ?? 0) > 0 ? 'Triage in Anomalies tab' : 'Healthy'}
          tone={(overview?.failed_payments?.last_30d_count ?? 0) > 0 ? 'warn' : 'good'}
          testid="admin-billing-kpi-failed"
        />
      </section>

      {/* Tabs */}
      <Tabs defaultValue="pricing" className="space-y-4" data-testid="admin-billing-tabs">
        <TabsList>
          <TabsTrigger value="pricing"      data-testid="admin-billing-tab-pricing">Pricing matrix</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="admin-billing-tab-subs">Subscriptions</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="admin-billing-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="webhooks"     data-testid="admin-billing-tab-webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="anomalies"    data-testid="admin-billing-tab-anomalies">
            Anomalies{anomalyTotal > 0 ? <span className="ml-1.5 text-amber-600">·{anomalyTotal}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="drift"        data-testid="admin-billing-tab-drift">
            Marketing drift{(drift?.drift_count ?? 0) > 0 ? <span className="ml-1.5 text-rose-600">·{drift.drift_count}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="refunds"      data-testid="admin-billing-tab-refunds">Refunds</TabsTrigger>
        </TabsList>

        {/* Pricing matrix tab */}
        <TabsContent value="pricing" className="space-y-4" data-testid="admin-billing-pricing-tab">
          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-ndor-gold" />
                Plan catalogue
              </CardTitle>
              <CardDescription>Source of truth — <code className="text-xs">core/pricing.py</code></CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                  <tr className="border-b border-ndor-edge">
                    <th className="text-left py-2 pr-3">Plan</th>
                    <th className="text-left py-2 pr-3">Period</th>
                    <th className="text-right py-2 pr-3">Price (GBP)</th>
                    <th className="text-right py-2 pr-3">Founding Member</th>
                    <th className="text-right py-2 pr-3">Included usage</th>
                    <th className="text-left py-2">Account modes</th>
                  </tr>
                </thead>
                <tbody className="font-mono-ui">
                  {(pricing?.plans || []).flatMap((p) =>
                    Object.entries(p.tiers || {}).map(([period, t]) => (
                      <tr key={`${p.plan_code}-${period}`} className="border-b border-ndor-edge" data-testid={`admin-billing-plan-${p.plan_code}-${period}`}>
                        <td className="py-2 pr-3 font-semibold capitalize">{p.label} <span className="text-ndor-ink-muted text-xs">({p.plan_code})</span></td>
                        <td className="py-2 pr-3 capitalize">{period}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{t.display_price || `£${t.price_gbp}`}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{t.early_access_display || (t.early_access_price_gbp ? `£${t.early_access_price_gbp}` : '—')}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{fmtNum(t.included_usage)}</td>
                        <td className="py-2 text-xs">{(p.allowed_account_modes || []).join(', ')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base">Top-up Credit packages</CardTitle>
              <CardDescription>RC 1.61 — Top-up Credits never expire (catalogue metadata)</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                  <tr className="border-b border-ndor-edge">
                    <th className="text-left py-2 pr-3">Code</th>
                    <th className="text-left py-2 pr-3">Label</th>
                    <th className="text-right py-2 pr-3">Usage</th>
                    <th className="text-right py-2 pr-3">Price</th>
                    <th className="text-right py-2">Validity</th>
                  </tr>
                </thead>
                <tbody className="font-mono-ui">
                  {(pricing?.extra_packages || []).map((p) => (
                    <tr key={p.code} className="border-b border-ndor-edge" data-testid={`admin-billing-topup-${p.code}`}>
                      <td className="py-2 pr-3 font-semibold">{p.code}</td>
                      <td className="py-2 pr-3 text-xs">{p.label}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtNum(p.usage_amount)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{p.display_price || `£${p.price_gbp}`}</td>
                      <td className="py-2 text-right tabular-nums">{p.expires_in_days ? `${p.expires_in_days}d` : 'No expiry'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base">Action credit costs</CardTitle>
              <CardDescription>
                Source of truth — admin-editable, persisted in{' '}
                <code className="text-xs">credit_action_prices</code>. Changes
                take effect on the next paid action (60-second cache).
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <ActionCostsTable
                meta={pricing?.action_costs_meta || {}}
                onChanged={() => load(true)}
              />
            </CardContent>
          </Card>

          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="h-4 w-4 text-ndor-ink-muted" />
                Stripe price map (env-bound)
              </CardTitle>
              <CardDescription>
                Built at backend startup from <code className="text-xs">STRIPE_PRICE_*</code> env vars.
                Empty entries indicate boot-validator drift — backend will refuse paid endpoints with 503.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-billing-stripe-map">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                  <tr className="border-b border-ndor-edge">
                    <th className="text-left py-2 pr-3">Price ID</th>
                    <th className="text-left py-2 pr-3">Kind</th>
                    <th className="text-left py-2 pr-3">Plan / Pack</th>
                    <th className="text-left py-2">Period / Flags</th>
                  </tr>
                </thead>
                <tbody className="font-mono-ui text-xs">
                  {(pricing?.stripe_price_map || []).length === 0 ? (
                    <tr><td colSpan={4} className="py-3 text-ndor-ink-muted italic">No price IDs resolved.</td></tr>
                  ) : (pricing?.stripe_price_map || []).map((m) => (
                    <tr key={m.price_id} className="border-b border-ndor-edge">
                      <td className="py-1.5 pr-3 break-all">{m.price_id}</td>
                      <td className="py-1.5 pr-3">{m.kind}</td>
                      <td className="py-1.5 pr-3">{m.plan_code || m.package_code || '—'}</td>
                      <td className="py-1.5">
                        {m.billing_period || ''} {m.early_access ? <Badge variant="outline" className="ml-1.5 border-ndor-gold/30 text-ndor-gold text-[10px]">FOUNDING</Badge> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions mix tab */}
        <TabsContent value="subscriptions" className="space-y-4" data-testid="admin-billing-subs-tab">
          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base">Active-subscription mix</CardTitle>
              <CardDescription>From <code className="text-xs">user_billing_link</code> (active / trialing / past_due)</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-billing-subs-mix">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                  <tr className="border-b border-ndor-edge">
                    <th className="text-left py-2 pr-3">Plan</th>
                    <th className="text-right py-2 pr-3">Monthly</th>
                    <th className="text-right py-2 pr-3">Annual</th>
                    <th className="text-right py-2">Unmapped</th>
                  </tr>
                </thead>
                <tbody className="font-mono-ui">
                  {Object.entries(overview?.subscriptions?.mix || {}).map(([plan, counts]) => (
                    <tr key={plan} className="border-b border-ndor-edge" data-testid={`admin-billing-mix-${plan}`}>
                      <td className="py-2 pr-3 font-semibold capitalize">{plan}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtNum(counts.monthly)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtNum(counts.annual)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtNum(counts.unknown)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="py-2 pr-3">Total active</td>
                    <td colSpan={3} className="py-2 text-right tabular-nums">{fmtNum(overview?.subscriptions?.active_total)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-3 text-xs text-ndor-ink-muted">
                Rows in the <em>Unmapped</em> column carry a <code className="text-[11px]">stripe_price_id</code>{' '}
                that doesn't resolve in the boot-time Stripe price map. Usually historical / test customers.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions tab */}
        <TabsContent value="transactions" className="space-y-3" data-testid="admin-billing-transactions-tab">
          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Receipt className="h-4 w-4 text-ndor-ink-muted" />
                Recent transactions
              </CardTitle>
              <CardDescription>{transactions.length} most recent rows from <code className="text-xs">payment_transactions</code></CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-billing-transactions-table">
                <thead className="text-[10px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                  <tr className="border-b border-ndor-edge">
                    <th className="text-left py-2 pr-3">Created</th>
                    <th className="text-left py-2 pr-3">Kind</th>
                    <th className="text-left py-2 pr-3">Plan / Pack</th>
                    <th className="text-right py-2 pr-3">Amount</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">User</th>
                    <th className="text-left py-2">Session</th>
                  </tr>
                </thead>
                <tbody className="font-mono-ui">
                  {transactions.length === 0 ? (
                    <tr><td colSpan={7} className="py-3 text-ndor-ink-muted italic">No transactions yet.</td></tr>
                  ) : transactions.map((t) => (
                    <tr key={t.id} className="border-b border-ndor-edge" data-testid={`admin-billing-tx-${t.session_id}`}>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{fmtTs(t.created_at)}</td>
                      <td className="py-1.5 pr-3">{t.kind}</td>
                      <td className="py-1.5 pr-3">{t.plan_code || t.package_code || '—'}{t.billing_period ? ` · ${t.billing_period}` : ''}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{fmtGbp(t.amount_pence)}</td>
                      <td className="py-1.5 pr-3"><StatusBadge status={t.status} /></td>
                      <td className="py-1.5 pr-3 text-[10px]">{(t.user_id || '').slice(0, 8)}…</td>
                      <td className="py-1.5 text-[10px] break-all max-w-[160px]">{(t.session_id || '').slice(0, 24)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webhooks tab */}
        <TabsContent value="webhooks" className="space-y-3" data-testid="admin-billing-webhooks-tab">
          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base">Recent Stripe webhook events</CardTitle>
              <CardDescription>{webhookEvents.length} most recent rows from <code className="text-xs">stripe_webhook_events</code></CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-billing-webhooks-table">
                <thead className="text-[10px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                  <tr className="border-b border-ndor-edge">
                    <th className="text-left py-2 pr-3">Received</th>
                    <th className="text-left py-2 pr-3">Type</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Session</th>
                    <th className="text-left py-2">Event</th>
                  </tr>
                </thead>
                <tbody className="font-mono-ui">
                  {webhookEvents.length === 0 ? (
                    <tr><td colSpan={5} className="py-3 text-ndor-ink-muted italic">No webhook events recorded.</td></tr>
                  ) : webhookEvents.map((e) => (
                    <tr key={e.event_id} className="border-b border-ndor-edge" data-testid={`admin-billing-event-${e.event_id}`}>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{fmtTs(e.received_at)}</td>
                      <td className="py-1.5 pr-3">{e.event_type}</td>
                      <td className="py-1.5 pr-3"><StatusBadge status={e.processing_status} /></td>
                      <td className="py-1.5 pr-3 text-[10px]">{(e.related_session_id || '').slice(0, 20)}…</td>
                      <td className="py-1.5 text-[10px] break-all max-w-[180px]">{e.event_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Anomalies tab */}
        <TabsContent value="anomalies" className="space-y-4" data-testid="admin-billing-anomalies-tab">
          {anomalyTotal === 0 ? (
            <Card className="border-emerald-500/40 bg-emerald-500/10">
              <CardContent className="p-6 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <p className="text-sm text-emerald-200">All clear — no Stripe-mismatch or stale-state anomalies detected.</p>
              </CardContent>
            </Card>
          ) : null}

          <AnomalyGroup
            title="Stale pending checkouts"
            help="Pending sessions older than 30 minutes. Usually buyers who abandoned the Stripe-hosted checkout. Safe to ignore at scale; monitor for spikes."
            rows={anomalies?.stale_pending || []}
            testid="admin-billing-anomaly-stale"
          />
          <AnomalyGroup
            title="Paid without finalisation timestamp"
            help="Status=paid/finalised but finalised_at is null. Indicates a missing finalisation write."
            rows={anomalies?.paid_no_finalise || []}
            testid="admin-billing-anomaly-no-finalise"
          />
          <AnomalyGroup
            title="Paid without webhook event"
            help="Money captured but no Stripe webhook event_id recorded. Usually means the polling-credit safety net fired before the webhook delivered. Investigate if recurring."
            rows={anomalies?.paid_no_event || []}
            testid="admin-billing-anomaly-no-event"
          />
          {(anomalies?.webhooks_rejected || []).length > 0 ? (
            <Card className="border-rose-500/40" data-testid="admin-billing-anomaly-rejected">
              <CardHeader>
                <CardTitle className="text-base text-rose-200">Rejected / tampered webhooks</CardTitle>
                <CardDescription>Signature failed or replayed past the 3-day retry window.</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                    <tr className="border-b border-ndor-edge">
                      <th className="text-left py-2 pr-3">Received</th>
                      <th className="text-left py-2 pr-3">Type</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-left py-2">Event ID</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono-ui">
                    {(anomalies?.webhooks_rejected || []).map((e) => (
                      <tr key={e.event_id} className="border-b border-ndor-edge">
                        <td className="py-1.5 pr-3 whitespace-nowrap">{fmtTs(e.received_at)}</td>
                        <td className="py-1.5 pr-3">{e.event_type}</td>
                        <td className="py-1.5 pr-3"><StatusBadge status={e.processing_status} /></td>
                        <td className="py-1.5 text-[10px] break-all">{e.event_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}

          {(anomalies?.plans_missing_in_stripe_map || []).length > 0 ? (
            <Card className="border-amber-500/40" data-testid="admin-billing-anomaly-missing-map">
              <CardHeader>
                <CardTitle className="text-base text-amber-200">Plan tiers missing in Stripe price map</CardTitle>
                <CardDescription>These plan/period pairs exist in <code className="text-xs">core/pricing.py</code> but no Stripe Price ID resolves to them. Check the boot-time validator output.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm font-mono-ui list-disc pl-5 space-y-0.5">
                  {(anomalies?.plans_missing_in_stripe_map || []).map((m) => (
                    <li key={`${m.plan_code}-${m.billing_period}`}>{m.plan_code} · {m.billing_period}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        {/* Marketing drift tab */}
        <TabsContent value="drift" className="space-y-3" data-testid="admin-billing-drift-tab">
          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-4 w-4 text-ndor-ink-muted" />
                Marketing site pricing drift
              </CardTitle>
              <CardDescription>
                Compares <code className="text-xs">/app/marketing/src/pages/pricing.astro</code>{' '}
                against <code className="text-xs">core/pricing.py</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!drift?.available ? (
                <p className="text-sm text-ndor-ink-secondary italic">
                  {drift?.reason || 'Marketing site not bundled with this deployment.'}
                </p>
              ) : drift.drift_count === 0 ? (
                <div className="flex items-center gap-2 text-emerald-200 text-sm">
                  <CheckCircle2 className="h-4 w-4" />
                  Marketing pricing matches the canonical backend catalogue.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-rose-200 text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    {drift.drift_count} drifted field{drift.drift_count === 1 ? '' : 's'}.
                  </div>
                  <table className="w-full text-sm" data-testid="admin-billing-drift-table">
                    <thead className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted">
                      <tr className="border-b border-ndor-edge">
                        <th className="text-left py-2 pr-3">Scope</th>
                        <th className="text-right py-2 pr-3">Backend (expected)</th>
                        <th className="text-right py-2 pr-3">Marketing (actual)</th>
                        <th className="text-left py-2">Kind</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono-ui">
                      {(drift.drift || []).map((d) => (
                        <tr key={d.scope} className="border-b border-ndor-edge">
                          <td className="py-1.5 pr-3">{d.scope}</td>
                          <td className="py-1.5 pr-3 text-right">{d.expected ?? '—'}</td>
                          <td className="py-1.5 pr-3 text-right text-rose-600">{d.actual ?? '—'}</td>
                          <td className="py-1.5">{d.kind}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-ndor-ink-muted">
                    Action: edit <code className="text-xs">/app/marketing/src/pages/pricing.astro</code>{' '}
                    to match, or update <code className="text-xs">core/pricing.py</code> AND propagate to Stripe Live.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Refunds placeholder tab */}
        <TabsContent value="refunds" className="space-y-3" data-testid="admin-billing-refunds-tab">
          <Card className="border-ndor-edge">
            <CardHeader>
              <CardTitle className="text-base">Refunds & disputes</CardTitle>
              <CardDescription>Tracked under roadmap item P1.9 — full integration deferred.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-ndor-ink-secondary">
              <p>
                Stripe currently emits <code className="text-xs">charge.refunded</code> and{' '}
                <code className="text-xs">charge.dispute.created</code> events, both subscribed
                to the live webhook endpoint. The handler audits the event into{' '}
                <code className="text-xs">stripe_webhook_events</code> but does not yet reverse{' '}
                the corresponding <code className="text-xs">payment_transactions</code> row or{' '}
                debit the <code className="text-xs">user_usage</code> bucket.
              </p>
              <p>
                Until P1.9 ships, refunds are processed manually inside the Stripe Dashboard and{' '}
                reconciled here from the Webhooks tab. Operators should:
              </p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Issue the refund in Stripe Dashboard.</li>
                <li>Look the original session up in the Transactions tab.</li>
                <li>
                  <strong>Granted credits are NOT auto-clawed-back today.</strong>{' '}
                  Look up the user under{' '}
                  <code className="text-xs">/admin/users</code> to inspect their current{' '}
                  <code className="text-xs">extra_usage_remaining</code>. The admin
                  usage-grant endpoint accepts <em>positive</em> integers only; there is{' '}
                  <strong>no UI clawback</strong> until P1.9 ships. If credits must be
                  reversed, coordinate with engineering for a service-role{' '}
                  <code className="text-xs">user_usage</code> adjustment and a paired{' '}
                  <code className="text-xs">credit_ledger</code> audit row.
                </li>
              </ol>
              <p className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/40 rounded p-2 leading-relaxed">
                <strong>RC 1.48.9 correction.</strong> Earlier copy on this tab suggested a
                negative-amount usage-grant — that path does not exist (the API rejects{' '}
                <code className="text-[11px] bg-ndor-surface-alt px-1 py-0.5 rounded">amount &lt;= 0</code>{' '}
                with HTTP 422). Until automated refund reversal lands, manual SQL is the only
                path and must be paired with an audit-ledger row.
              </p>
              <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-200">
                Read-only placeholder · P1.9 follow-up
              </Badge>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Action Costs editable table — P1.8.A admin source of truth.
// ─────────────────────────────────────────────────────────────
function ActionCostsTable({ meta, onChanged }) {
  return (
    <table className="w-full text-sm" data-testid="admin-billing-action-costs-table">
      <thead className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted">
        <tr className="border-b border-ndor-edge">
          <th className="text-left py-2 pr-3">Action</th>
          <th className="text-right py-2 pr-3">Credits per call</th>
          <th className="text-right py-2 pr-3">Source</th>
          <th className="text-right py-2" title="Typical wholesale OpenAI cost per call (RC 1.47 · calibrated 2026-05-01 against the currently configured NDOR primary model)">Internal cost</th>
        </tr>
      </thead>
      <tbody className="font-mono-ui">
        {Object.entries(meta || {}).map(([action, info]) => (
          <ActionCostRow
            key={action}
            action={action}
            info={info}
            onChanged={onChanged}
          />
        ))}
      </tbody>
    </table>
  );
}

function ActionCostRow({ action, info, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(info?.cost ?? 0));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset draft when the row changes externally (e.g. after refresh).
  useEffect(() => {
    if (!editing) setDraft(String(info?.cost ?? 0));
  }, [info?.cost, editing]);

  const beginEdit = () => { setErr(null); setEditing(true); };
  const cancelEdit = () => {
    setErr(null);
    setEditing(false);
    setDraft(String(info?.cost ?? 0));
  };

  const validate = () => {
    if (draft === '' || draft === null || draft === undefined) return 'Value required.';
    const n = Number(draft);
    if (!Number.isFinite(n)) return 'Must be a number.';
    if (!Number.isInteger(n)) return 'Must be an integer.';
    if (n < 0) return 'Must be ≥ 0.';
    if (n > 10000) return 'Must be ≤ 10000.';
    return null;
  };

  // RC 1.48.9 P0-1 — every action-cost change is platform-wide pricing.
  // Open a confirm modal before firing the PUT so a fat-finger can't
  // re-price the platform on a single click. Mirrors the spend-bounded
  // confirm pattern on /admin/settings.
  const requestSave = () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setConfirmOpen(true);
  };

  const save = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    const n = Number(draft);
    setSaving(true); setErr(null);
    setConfirmOpen(false);
    try {
      await apiFetch(`/api/admin/credit-prices/${encodeURIComponent(action)}`, {
        method: 'PUT',
        body: { cost: n },
      });
      toast.success(`Updated · ${action} → ${n} credits`);
      setEditing(false);
      if (onChanged) onChanged();
    } catch (ex) {
      const msg = ex?.message || 'Save failed.';
      setErr(msg);
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  const sourceBadge = info?.source === 'db' ? (
    <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[10px] uppercase tracking-wide">
      DB
    </Badge>
  ) : (
    <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-200 text-[10px] uppercase tracking-wide">
      Default
    </Badge>
  );

  return (
    <>
      <tr className="border-b border-ndor-edge" data-testid={`admin-billing-action-${action}`}>
        <td className="py-2 pr-3 align-middle">{action}</td>
        <td className="py-2 pr-3 text-right tabular-nums align-middle">
          {!editing ? (
            <div className="flex items-center justify-end gap-2">
              <span data-testid={`admin-billing-action-cost-${action}`}>{info?.cost}</span>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={beginEdit}
                aria-label={`Edit ${action} cost`}
                data-testid={`admin-billing-action-edit-${action}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-1.5" data-testid={`admin-billing-action-editor-${action}`}>
              <Input
                type="number"
                step={1}
                min={0}
                max={10000}
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setErr(null); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') requestSave();
                  if (e.key === 'Escape') cancelEdit();
                }}
                disabled={saving}
                autoFocus
                className="h-8 w-20 text-right tabular-nums"
                data-testid={`admin-billing-action-input-${action}`}
              />
              <Button
                size="sm"
                onClick={requestSave}
                disabled={saving}
                className="h-8 bg-ndor-ink text-ndor-canvas hover:bg-ndor-ink/90"
                data-testid={`admin-billing-action-save-${action}`}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEdit}
                disabled={saving}
                className="h-8"
                data-testid={`admin-billing-action-cancel-${action}`}
              >
                <XIcon className="h-3 w-3" />
              </Button>
            </div>
          )}
          {err ? (
            <p className="text-[11px] text-rose-600 mt-1 text-right" data-testid={`admin-billing-action-error-${action}`}>{err}</p>
          ) : null}
        </td>
        <td className="py-2 pr-3 text-right">{sourceBadge}</td>
        <td
          className="py-2 text-right tabular-nums text-ndor-ink-muted"
          title="Typical wholesale OpenAI cost per call · admin visibility only"
          data-testid={`admin-billing-action-internal-cost-${action}`}
        >
          {typeof info?.internal_cost_usd === 'number'
            ? `$${info.internal_cost_usd.toFixed(4)}`
            : '—'}
        </td>
      </tr>

      {/* RC 1.48.9 P0-1 — confirm modal so a fat-finger can't re-price the platform */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !saving && setConfirmOpen(o)}>
        <DialogContent
          className="sm:max-w-[440px]"
          data-testid={`admin-billing-action-confirm-${action}`}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Confirm price change
            </DialogTitle>
            <DialogDescription className="text-sm text-ndor-ink-secondary leading-relaxed pt-2 space-y-2">
              <span className="block">
                You're about to change the platform-wide Credit cost for{' '}
                <strong className="text-ndor-ink">{action}</strong>.
              </span>
              <span className="block">
                <code className="text-[11px] px-1.5 py-0.5 rounded bg-ndor-surface-alt">{action}</code>
                : <span className="font-mono-ui">{info?.cost ?? '—'}</span>{' '}
                → <span className="font-mono-ui font-semibold text-ndor-ink">{draft}</span>{' '}
                Credits per call
              </span>
              <span className="block text-[12px] text-amber-200 bg-amber-500/10 border border-amber-500/40 rounded p-2 mt-2">
                This change takes effect on every paid call within 60 seconds.
                Affects every user on the platform.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
              data-testid={`admin-billing-action-confirm-cancel-${action}`}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={save}
              disabled={saving}
              className="bg-ndor-ink text-ndor-canvas hover:bg-ndor-ink/90"
              data-testid={`admin-billing-action-confirm-save-${action}`}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
              Confirm save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Anomaly group helper
// ─────────────────────────────────────────────────────────────
function AnomalyGroup({ title, help, rows, testid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card className="border-amber-500/40" data-testid={testid}>
      <CardHeader>
        <CardTitle className="text-base text-amber-200 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {title}
          <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-200 ml-1.5">{rows.length}</Badge>
        </CardTitle>
        <CardDescription>{help}</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-[0.14em] text-ndor-ink-muted">
            <tr className="border-b border-ndor-edge">
              <th className="text-left py-2 pr-3">Created</th>
              <th className="text-left py-2 pr-3">Kind</th>
              <th className="text-right py-2 pr-3">Amount</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-left py-2">Session</th>
            </tr>
          </thead>
          <tbody className="font-mono-ui">
            {rows.slice(0, 20).map((r) => (
              <tr key={r.id} className="border-b border-ndor-edge">
                <td className="py-1.5 pr-3 whitespace-nowrap">{fmtTs(r.created_at)}</td>
                <td className="py-1.5 pr-3">{r.kind}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{fmtGbp(r.amount_pence)}</td>
                <td className="py-1.5 pr-3"><StatusBadge status={r.status} /></td>
                <td className="py-1.5 text-[10px] break-all max-w-[180px]">{(r.session_id || '').slice(0, 24)}…</td>
              </tr>
            ))}
            {rows.length > 20 ? (
              <tr><td colSpan={5} className="py-2 text-ndor-ink-muted italic text-center">+{rows.length - 20} more not shown</td></tr>
            ) : null}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

